import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  dreamTeamAsPickLight,
  getEventDreamTeam,
  getEventDreamTeamFormation,
  getOverallDreamTeam,
  getOverallDreamTeamFormation,
} from "core-integration/src/store/dream-teams/reducers";
import {
  fetchEventDreamTeam,
  fetchOverallDreamTeam,
} from "core-integration/src/store/dream-teams/thunks";
import {
  getElementsById,
  getElementsEventDataById,
} from "core-integration/src/store/elements/reducers";
import {
  fetchEventLive,
  showElementSummary,
} from "core-integration/src/store/elements/thunks";
import {
  getCurrentEvent,
  getEventsById,
} from "core-integration/src/store/events/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { PagerButton } from "plos/src/components/buttons/PagerButton";
import { Fixtures } from "plos/src/components/Fixtures";
import PageTitle from "plos/src/components/PageTitle";
import Pitch from "plos/src/components/Pitch";
import Subheading from "plos/src/components/Subheading";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { entryEventMain, leftSidebarLayout } from "plos/src/layouts";
import { edgeToEdge } from "plos/src/styles";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { OverlayProvider } from "../../../contexts/OverlayContext";
import { usePitchView } from "../../../hooks/usePitchView";
import { isBlankWeek } from "../../../utils/events";
import EntryInfo from "../../EntryInfo";
import { HelmetHead } from "../../HelmetHead";
import { PitchControls } from "../../PitchControls";
import { NotFound } from "../../Routes";
import ElementExplainSheet from "../ElementExplainSheet";
import EntryEventTable from "../EntryEventTable";
import {
  eventHeaderWrapper,
  eventHeading,
  eventPager,
  teamPitchWrap,
} from "../myTeam.css";
import PitchFormation from "../PitchFormation";
import { overallWrapper } from "./dreamTeam.css";
import DreamTeamHeader from "./DreamTeamScoreboard";

const DreamTeam = () => {
  const { t } = useTranslation("dreamTeam");

  const dispatch = useDispatch<ThunkDispatch>();
  const { eventId = 0 } = useParams();
  const eventIdNumber = Number(eventId);

  const player = useSelector(getPlayerData);
  const entryId = player?.entry;

  const [elementForMenu, setElementForMenu] = useState(0);
  const { activeView, handleViewChange } = usePitchView();

  const data = useSelector((state: RootState) =>
    eventIdNumber
      ? getEventDreamTeam(state, eventIdNumber)
      : getOverallDreamTeam(state),
  );
  const elementsById = useSelector(getElementsById);
  const elementsDataById = useSelector((state: RootState) =>
    getElementsEventDataById(state, eventIdNumber),
  );

  const event = useSelector(
    (state: RootState) => getEventsById(state)[eventIdNumber],
  );
  const formation = useSelector((state: RootState) =>
    eventIdNumber
      ? getEventDreamTeamFormation(state, eventIdNumber)
      : getOverallDreamTeamFormation(state),
  );
  const now = useSelector(getCurrentEvent);

  const handleShowMenuForElement = (element: number) => {
    setElementForMenu(element);
  };

  const handleClosePlayerSheet = () => {
    setElementForMenu(0);
  };

  useEffect(() => {
    const fetchData = () => {
      if (event) {
        dispatch(fetchEventDreamTeam(event.id));
        dispatch(fetchEventLive(event.id));
      } else {
        dispatch(fetchOverallDreamTeam());
      }
    };
    fetchData();
  }, [dispatch, event]);

  const showElementDialog = useCallback(
    (elementId: number) => dispatch(showElementSummary(elementId)),
    [dispatch],
  );

  useEffect(() => {
    // For the overall team we just show the standard element dialog as there
    // is no explain
    if (elementForMenu && !event) {
      showElementDialog(elementForMenu);
    }
  }, [dispatch, elementForMenu, event, showElementDialog]);

  if (!now) {
    return <NotFound />;
  }
  if (!data) {
    return null;
  }
  const picks = dreamTeamAsPickLight(data.team);

  return (
    <OverlayProvider defaultOverlay="points" page="dreamTeam">
      <>
        <HelmetHead
          title={
            event
              ? t(
                  "metadata.title.week",
                  "Team of the Week - Gameweek {{eventId}} | Fantasy Premier League",
                  { eventId: event.id },
                )
              : t(
                  "metadata.title.season",
                  "Team of the Season 2026/27 | Fantasy Premier League",
                )
          }
          description={
            event
              ? t(
                  "metadata.description.week",
                  "To view the Fantasy Premier League Team of Gameweek {{eventId}}, visit the official website of the Premier League.",
                  { eventId: event.id },
                )
              : t(
                  "metadata.description.season",
                  "To view the Fantasy Premier League Team of the Season 2026/27, visit the official website of the Premier League.",
                )
          }
        />
        <div className={leftSidebarLayout}>
          <div className={entryEventMain}>
            <section>
              <SurfaceContainer>
                <div className={teamPitchWrap}>
                  <PageTitle
                    title={
                      event
                        ? t("teamOfTheWeek", "Team of the Week")
                        : t("teamOfTheSeason", "Team of the Season")
                    }
                  />
                  {event ? (
                    <div className={eventHeaderWrapper}>
                      <div className={eventHeading}>
                        <h2>{event.name}</h2>
                      </div>
                      {(event.id > 1 || event.id < now.id) && (
                        <div className={eventPager}>
                          <PagerButton
                            url={`/team-of-the-week/${event.id - 1}`}
                            direction="left"
                            isDisabled={event.id <= 1}
                            label={t("previousGameweek", "Previous gameweek")}
                          />
                          <PagerButton
                            url={`/team-of-the-week/${event.id + 1}`}
                            direction="right"
                            isDisabled={event.id >= now.id}
                            label={t("nextGameweek", "Next gameweek")}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={overallWrapper}>
                      <Subheading>{t("overall", "Overall")}</Subheading>
                    </div>
                  )}

                  {(!event || !isBlankWeek(event.id)) && (
                    <>
                      <DreamTeamHeader event={event} data={data} now={now} />
                      <PitchControls
                        activeView={activeView}
                        handleViewChange={handleViewChange}
                      />

                      {activeView === "pitch" && (
                        <div className={edgeToEdge}>
                          <Pitch sponsor="default">
                            <PitchFormation
                              chipName={""}
                              eventId={event?.id}
                              formation={formation}
                              picks={picks}
                              renderDreamTeam={() => null}
                              renderElementMenu={handleShowMenuForElement}
                            />
                          </Pitch>
                        </div>
                      )}
                      {activeView === "list" && (
                        <EntryEventTable
                          picks={picks}
                          elementsById={elementsById}
                          renderElementMenu={handleShowMenuForElement}
                          dataById={elementsDataById}
                          chipName={null}
                        />
                      )}
                    </>
                  )}

                  {event && elementForMenu ? (
                    <ElementExplainSheet
                      elementId={elementForMenu}
                      eventId={event.id}
                      handleClose={handleClosePlayerSheet}
                      open={Boolean(elementForMenu)}
                    />
                  ) : null}
                </div>
              </SurfaceContainer>
            </section>
            <section>
              <SurfaceContainer>
                <Fixtures eventId={eventIdNumber} />
              </SurfaceContainer>
            </section>
          </div>

          {entryId && (
            <section>
              <SurfaceContainer>
                <EntryInfo entryId={entryId} />
              </SurfaceContainer>
            </section>
          )}
        </div>
      </>
    </OverlayProvider>
  );
};

export default DreamTeam;
